import { useCallback, useEffect, useState } from 'react';
import {
  createStorageLocation,
  listStorageLocations
} from '../Api/Inventory.api';
import { MOCK_LOCATIONS } from '../mockData';
import type {
  CreateStorageLocationPayload,
  StorageLocationNode
} from '../Types';
import {
  appendLocationToTree,
  filterUserVisibleStorageLocations,
  flatLocationsToTree
} from '../Utils/StorageLocation.utils';

interface UseStorageLocationsResult {
  locations: StorageLocationNode[];
  loading: boolean;
  error: string | null;
  creating: boolean;
  createLocation: (
    payload: CreateStorageLocationPayload
  ) => Promise<StorageLocationNode>;
  reload: () => Promise<void>;
}

export const useStorageLocations = (
  sessionToken: string | null,
  branchId: string | undefined,
  isLive: boolean
): UseStorageLocationsResult => {
  const [locations, setLocations] = useState<StorageLocationNode[]>(() =>
    isLive ? [] : flatLocationsToTree(MOCK_LOCATIONS)
  );
  const [loading, setLoading] = useState(isLive);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!isLive) {
      setLocations(flatLocationsToTree(MOCK_LOCATIONS));
      setError(null);
      return;
    }

    if (!sessionToken || !branchId) {
      setLocations([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const tree = await listStorageLocations({
        accessToken: sessionToken,
        branchId
      });
      setLocations(filterUserVisibleStorageLocations(tree));
    } catch (err: unknown) {
      setLocations([]);
      setError(
        err instanceof Error ? err.message : 'Failed to load storage locations'
      );
    } finally {
      setLoading(false);
    }
  }, [branchId, isLive, sessionToken]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const createLocation = useCallback(
    async (
      payload: CreateStorageLocationPayload
    ): Promise<StorageLocationNode> => {
      setCreating(true);
      setError(null);
      try {
        if (!isLive) {
          const created: StorageLocationNode = {
            id: crypto.randomUUID(),
            name: payload.name.trim(),
            code: payload.code?.trim() || null,
            location_type: payload.locationType,
            sort_order: 0,
            active: true,
            children: []
          };
          setLocations(prev =>
            appendLocationToTree(
              prev,
              created,
              payload.parentLocationId ?? null
            )
          );
          return created;
        }

        if (!sessionToken || !branchId) {
          throw new Error(
            'Open this page from your inventory invite link to add storage areas'
          );
        }

        const created = await createStorageLocation(payload, {
          accessToken: sessionToken,
          branchId
        });
        const tree = await listStorageLocations({
          accessToken: sessionToken,
          branchId
        });
        setLocations(filterUserVisibleStorageLocations(tree));
        return created;
      } finally {
        setCreating(false);
      }
    },
    [branchId, isLive, sessionToken]
  );

  return { locations, loading, error, creating, createLocation, reload };
};
